/*
 * patient (zod)
 */
import { globalCreateExclude, globalUpdateExclude } from "../utils/constants"
import { createInsertSchema, createSelectSchema } from "drizzle-zod"
import * as z from "zod"
import { patients } from "./patient.schema"

/* Select */
export type Patient = z.infer<typeof selectPatientSchema>
export const selectPatientSchema = createSelectSchema(patients)

export const SERVICE_TYPES = [
	"veteran",
	"active_military",
	"first_responder",
	"civilian",
] as const

export type ServiceType = typeof SERVICE_TYPES[number]

export const CLIENT_STATUSES = [
	"review_required",
	"education_required",
	"education_scheduled",
	"prescription_required",
	"prescription_scheduled",
	"active",
	"sla_required",
	"sla_scheduled",
	"renewal_required",
	"not_active",
] as const

export type ClientStatus = typeof CLIENT_STATUSES[number]

/* Filters */
export type PatientFilters = z.infer<typeof patientFiltersSchema>
export const patientFiltersSchema = z.object({
	id: z.string().optional(),
	userId: z.string().optional(),
	practitionerId: z.string().optional(),
	active: z.boolean().optional(),
	clientStatus: z.enum(CLIENT_STATUSES).optional(),
})

const dateCoerce = {
	dateOfBirth: z.coerce.date({ required_error: "Date of Birth is required" }),
	vacStartDate: z.coerce.date().optional().nullable(),
	vacApprovalEndDate: z.coerce.date().optional().nullable(),
}

const kNumberValidation = z
	.string()
	.min(1, "K Number is required")
	.regex(/^K\d{7}$/, "Must be K followed by 7 digits (e.g. K1234567)")

const requiredFields = {
	firstName: z.string().min(1, "First name is required"),
	lastName: z.string().min(1, "Last name is required"),
	gender: z.string().min(1, "Gender is required"),
	phone: z.string().min(1, "Phone is required"),
	email: z.string().min(1, "Email is required"),
	emergencyContactName: z.string().min(1, "Emergency contact name is required"),
	emergencyContactRelationship: z.string().min(1, "Relationship is required"),
	emergencyContactPhone: z.string().min(1, "Emergency contact phone is required"),
}

/* Create */
export const createPatientSchema = createInsertSchema(patients)
	.partial(globalCreateExclude)
	.extend({ ...dateCoerce, kNumber: kNumberValidation, ...requiredFields })
export type CreatePatient = z.infer<typeof createPatientSchema>

/* Update */
export const updatePatientSchema = createInsertSchema(patients)
	.omit(globalUpdateExclude)
	.partial()
	.required({ id: true })
	.extend({ ...dateCoerce, kNumber: kNumberValidation, ...requiredFields })
export type UpdatePatient = z.infer<typeof updatePatientSchema>
